import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/solid';
import axiosInstance from '../../api/axiosInstance';
import logger from '../../../utils/logger';
import StepBasics from './steps/StepBasics';
import StepWorkspace from './steps/StepWorkspace';
import StepHire from './steps/StepHire';
import StepTeach from './steps/StepTeach';
import StepFirstGoal from './steps/StepFirstGoal';

// Guided customer onboarding (Phase 1, 2026-08). Five steps; each one calls a
// real backend door and marks progress server-side, so leaving and coming
// back resumes where the customer stopped (GET /api/business/onboarding/status).
const STEPS = [
  { id: 'basics', label: 'About you' },
  { id: 'workspace', label: 'Workspace' },
  { id: 'agent', label: 'Hire your agent' },
  { id: 'knowledge', label: 'Teach it' },
  { id: 'firstGoal', label: 'First goal' },
];

const OnboardingWizard = ({ showNotification }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const domainOnly = searchParams.get('setup') === 'domain';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  // Facts a step learns that later steps need (e.g. the hired agent's email).
  const [wizardState, setWizardState] = useState({});

  const loadStatus = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/business/onboarding/status');
      const data = res.data?.data || {};
      setStatus(data);
      return data;
    } catch (error) {
      logger.error('Failed to load onboarding status:', error);
      showNotification?.('Could not load onboarding status', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    (async () => {
      const data = await loadStatus();
      if (!data) return;
      const steps = data.steps || {};
      const firstIncomplete = STEPS.findIndex((s) => !steps[s.id]);
      if (firstIncomplete === -1) {
        setFinished(true);
        setStepIndex(STEPS.length - 1);
      } else {
        setStepIndex(firstIncomplete);
      }
    })();
  }, [loadStatus]);

  const markProgress = async (stepId, details = {}) => {
    try {
      await axiosInstance.post('/api/business/onboarding/progress', { step: stepId, ...details });
      return true;
    } catch (error) {
      logger.error('Failed to save onboarding progress:', error);
      showNotification?.(error.response?.data?.message || 'Could not save your progress. Please retry.', 'error');
      return false;
    }
  };

  const advance = async (patch = {}) => {
    setWizardState((s) => ({ ...s, ...patch }));
    await loadStatus();
    if (stepIndex >= STEPS.length - 1) {
      setFinished(true);
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
      </div>
    );
  }
  if (!status) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center text-gray-600 dark:text-gray-300">
        Could not load your onboarding status.{' '}
        <button className="text-orange-600 underline" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const stepProps = {
    status,
    wizardState,
    showNotification,
    markProgress,
    onDone: advance,
    onBack: stepIndex > 0 ? goBack : null,
    reloadStatus: loadStatus,
  };

  if (domainOnly) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Set up your workspace email</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Use your own domain or get an Aiinak-provided address. You can add people as soon as your domain is ready.</p>
        <div className="bg-white dark:bg-[#141414] border border-gray-200 dark:border-gray-800 rounded-xl p-6 sm:p-8">
          <StepWorkspace {...stepProps} onBack={() => navigate('/dashboard')} onDone={() => navigate('/users')} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Progress rail */}
      <div className="flex items-center justify-between mb-10">
        {STEPS.map((s, i) => {
          const done = status.steps?.[s.id] || i < stepIndex;
          const active = i === stepIndex && !finished;
          return (
            <React.Fragment key={s.id}>
              <div className="flex flex-col items-center min-w-0">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                    done
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : active
                        ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                        : 'border-gray-300 dark:border-gray-700 text-gray-400'
                  }`}
                >
                  {done ? <CheckIcon className="w-5 h-5" /> : i + 1}
                </div>
                <span
                  className={`mt-2 text-xs text-center ${
                    active ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-5 ${done ? 'bg-orange-500' : 'bg-gray-200 dark:bg-gray-800'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="bg-white dark:bg-[#141414] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-6 sm:p-8">
        {finished ? (
          <FinishScreen status={status} wizardState={wizardState} navigate={navigate} />
        ) : (
          <>
            {stepIndex === 0 && <StepBasics {...stepProps} />}
            {stepIndex === 1 && <StepWorkspace {...stepProps} />}
            {stepIndex === 2 && <StepHire {...stepProps} />}
            {stepIndex === 3 && <StepTeach {...stepProps} />}
            {stepIndex === 4 && <StepFirstGoal {...stepProps} />}
          </>
        )}
      </div>
    </div>
  );
};

const FinishScreen = ({ status, wizardState, navigate }) => {
  const agent = wizardState.agentName
    ? { name: wizardState.agentName, email: wizardState.agentEmail }
    : status.agents?.[0]
      ? { name: status.agents[0].name, email: status.agents[0].email }
      : null;
  return (
    <div className="text-center py-6">
      <div className="w-14 h-14 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
        <CheckIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        {agent ? `${agent.name} is on the job` : 'Setup complete'}
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
        {agent
          ? `Your agent is working on its first goal and will report back by email${agent.email ? ` from ${agent.email}` : ''}. Every task, action, and promise shows up in its receipts.`
          : 'Your workspace is ready.'}
      </p>
      <div className="flex items-center justify-center gap-3">
        <a
          href="https://agents.aiinak.com"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium"
        >
          See your agent working
        </a>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Go to dashboard
        </button>
      </div>
    </div>
  );
};

export default OnboardingWizard;
